import React from "react";
import { useTheme } from "@mui/material";

// Utility function to format long names
const formatLongName = (name, maxLength = 25) => {
  if (!name || name.length <= maxLength) {
    return name;
  }

  const words = name.trim().split(' ');

  // If only one word, truncate it
  if (words.length === 1) {
    return name.length > maxLength ?
      `${name.substring(0, Math.floor(maxLength / 2))}...${name.substring(name.length - Math.floor(maxLength / 2))}` :
      name;
  }

  // If multiple words, show first word and last word (no dots)
  const firstWord = words[0];
  const lastWord = words[words.length - 1];

  // If first + last word is still too long, truncate them
  if ((firstWord + ' ' + lastWord).length > maxLength) {
    const availableLength = maxLength - 1; // Reserve 1 char for space
    const halfLength = Math.floor(availableLength / 2);
    return `${firstWord.substring(0, halfLength)} ${lastWord.substring(lastWord.length - (availableLength - halfLength))}`;
  }

  // Show first word + space + last word (no dots)
  return `${firstWord} ${lastWord}`;
};


const FormattedText = ({ text, taggedUsers = [] }) => {
  const theme = useTheme();
  if (!text) return null;

  const lines = text.split("\n");
  return lines.map((line, index) => (
    <React.Fragment key={index}>
      {line.trim() === "" ? (
        // Only render <br /> for empty lines, but not at the end
        index < lines.length - 1 && <br />
      ) : (
        line.split(" ").map((word, wordIndex) => {
          let formattedWord = word.trim();
          if (!formattedWord) return null;

          if (/^(https?:\/\/|www\.)/.test(formattedWord)) {
            const link = formattedWord.startsWith("www.")
              ? `https://${formattedWord}`
              : formattedWord;
            return (
              <React.Fragment key={wordIndex}>
                <a
                  href={link}
                  style={{
                    color: theme.palette.info.main,
                    wordBreak: "break-word",
                  }}
                  target="_blank"
                  rel="noreferrer"
                >
                  {formattedWord}
                </a>
                {wordIndex < line.split(" ").length - 1 && "   "}
              </React.Fragment>
            );
          }

          if (formattedWord.startsWith("#")) {
            return (
              <React.Fragment key={wordIndex}>
                <a
                  href={`/home/hashtag/${formattedWord.slice(1)}`}
                  style={{
                    color: theme.palette.info.main,
                    textDecoration: "none",
                  }}
                >
                  {formattedWord}
                </a>
                {wordIndex < line.split(" ").length - 1 && "   "}
              </React.Fragment>
            );
          }

          if (formattedWord.startsWith("@")) {
            const username = formattedWord.slice(1);
            // Find the tagged user by username
            const taggedUser = taggedUsers.find(user => user.username === username);
           // console.log("Tagged user for", username, ":", taggedUser);

            if (taggedUser) {
              const displayUsername = formatLongName(taggedUser.full_name, 20);
              return (
                <React.Fragment key={wordIndex}>
                  <a
                    href={`/profile/${username}`}
                    style={{
                      fontWeight: "bold",
                      color: theme.palette.info.main,
                      textDecoration: "none",
                    }}
                    title={taggedUser.full_name}
                  >
                    {/* {taggedUser.full_name} */}
                    {displayUsername}
                  </a>
                  {wordIndex < line.split(" ").length - 1 && "\t"}
                </React.Fragment>
              );
            }

            // Fallback to username if tagged user not found
            return (
              <React.Fragment key={wordIndex}>
                  {formattedWord}
                {wordIndex < line.split(" ").length - 1 && "\t"}
              </React.Fragment>
            );
          }

          return (
            <React.Fragment key={wordIndex}>
              <span>{formattedWord}</span>
              {wordIndex < line.split(" ").length - 1 && "   "}
            </React.Fragment>
          );
        })
      )}
      {/* Only add <br /> if there's content and it's not the last line */}
      {line.trim() !== "" && index < lines.length - 1 && <br />}
    </React.Fragment>
  ));
};

export default FormattedText;